import React, { useState, useRef } from "react";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { useNavigate } from "react-router-dom";
import type { Swiper as SwiperType } from 'swiper';

interface Service {
  label: string;
  slug: string;
  image_url?: string;
  professions?: string[];
}

interface ServicesSectionProps {
  verifiedProfiles?: any[];
  selectedService?: Service | null;
  onServiceClick?: (service: Service) => void;
  services?: Service[];
  servicesLoading?: boolean;
}

function getServiceImage(service: Service, verifiedProfiles = []) {
  // Log all professions for debugging
  console.log('All verified professions:', verifiedProfiles.map(p => p.profession));
  // Case-insensitive, trimmed matching
  const matches = verifiedProfiles.filter(profile =>
    service.professions && service.professions.some(
      p => p.toLowerCase().trim() === (profile.profession || '').toLowerCase().trim()
    )
  );
  console.log('Service:', service.label, 'Matching profiles:', matches);

  if (matches.length === 0) {
    console.log('No matches for service:', service.label, 'using fallback image:', service.image_url);
    return service.image_url;
  }

  const profile = matches.sort((a, b) => {
    const aDate = a.updated_at ? new Date(a.updated_at).getTime() : 0;
    const bDate = b.updated_at ? new Date(b.updated_at).getTime() : 0;
    return bDate - aDate;
  })[0];

  console.log('Selected profile for service', service.label, profile);

  if (profile.portfolio_items && profile.portfolio_items.length > 0) {
    const sorted = profile.portfolio_items
      .filter(item => item.status === 'published' && item.created_at)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    const lastAddedItem = sorted[0];
    console.log('Last added item for service', service.label, lastAddedItem);

    if (lastAddedItem) {
      if (lastAddedItem.images && lastAddedItem.images.length > 0) {
        console.log('Using image:', lastAddedItem.images[0].url);
        return lastAddedItem.images[0].url;
      }
      if (lastAddedItem.image_url) {
        console.log('Using image_url:', lastAddedItem.image_url);
        return lastAddedItem.image_url;
      }
    }
  }
  console.log('Using avatar or fallback for service', service.label, profile.avatar_url || service.image_url);
  return profile.avatar_url || service.image_url;
}

const ServicesSection: React.FC<ServicesSectionProps> = ({
  verifiedProfiles = [],
  selectedService = null,
  onServiceClick,
  services = [],
  servicesLoading = false
}) => {
  const navigate = useNavigate();
  const [hasScrolled, setHasScrolled] = useState(false);
  const swiperRef = useRef<SwiperType | null>(null);

  if (servicesLoading) {
    return <div className="py-8 text-center text-gray-400">Loading services...</div>;
  }
  if (!services || services.length === 0) {
    return null;
  }

  const handleSlideChange = (swiper: SwiperType) => {
    // Show left arrow if we're not at the beginning
    setHasScrolled(swiper.activeIndex > 0);
  };

  return (
    <section className="mb-8">
      <div className="border-t border-gray-200 mb-8"></div>
      <div className="flex items-center gap-2 mb-1">
        <h2 className="text-base md:text-xl font-bold opacity-70 text-[#09090B]">Services</h2>
      </div>
      <p className="text-[#6B7280] text-xs md:text-sm mb-6">Get the right service for your next product or brand</p>
      {/* Swiper only on desktop (md and up) */}
      <div className="relative group h-full hidden md:block">
        {/* Custom Swiper navigation buttons styled as chevrons, faded by default, fully visible on hover */}
        <button
          className={`swiper-service-prev transition-all duration-300 absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white shadow flex items-center justify-center border border-gray-200 hover:bg-gray-100 ${
            hasScrolled ? 'opacity-60 group-hover:opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          aria-label="Previous"
          type="button"
        >
          <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
            <path d="M12 15l-5-5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <button
          className="swiper-service-next opacity-60 group-hover:opacity-100 transition-opacity duration-200 absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white shadow flex items-center justify-center border border-gray-200 hover:bg-gray-100"
          aria-label="Next"
          type="button"
        >
          <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
            <path d="M8 5l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <Swiper
          modules={[Navigation]}
          navigation={{
            prevEl: ".swiper-service-prev",
            nextEl: ".swiper-service-next",
          }}
          spaceBetween={16}
          slidesPerView={3.4} // Show more of the next card
          breakpoints={{
            640: { slidesPerView: 2.4 },
            768: { slidesPerView: 3.0 },
            1024: { slidesPerView: 3.4 },
          }}
          style={{ paddingBottom: 24, paddingRight: 24 }} // Reduced right padding to show more of peeking card
          onSlideChange={handleSlideChange}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            // Initialize the state based on current position
            setHasScrolled(swiper.activeIndex > 0);
          }}
        >
          {services.map(service => (
            <SwiperSlide key={service.label} className="max-w-xs">
              <div
                className={`bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col h-full cursor-pointer transition-all duration-200 ${selectedService && selectedService.slug === service.slug ? 'ring-2 ring-accent border-accent' : 'hover:shadow-lg'}`}
                onClick={() => navigate(`/services/${service.slug}`)}
              >
                <div className="p-4 flex-1 flex flex-col justify-end">
                  <h3 className="font-semibold text-base mb-1 truncate">{service.label}</h3>
                </div>
                <div className="aspect-square w-full bg-gray-50 flex items-center justify-center overflow-hidden">
                  <img src={service.image_url} alt={service.label} className="object-cover w-full h-full" />
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      {/* 2-column grid only on mobile (below md) */}
      <div className="grid grid-cols-2 gap-4 md:hidden">
        {services.map(service => (
          <div
            key={service.label}
            className={`bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col cursor-pointer transition-all duration-200 ${selectedService && selectedService.slug === service.slug ? 'ring-2 ring-accent border-accent' : 'hover:shadow-lg'}`}
            onClick={() => navigate(`/services/${service.slug}`)}
          >
            <div className="aspect-square w-full bg-gray-50 flex items-center justify-center overflow-hidden">
              <img src={service.image_url} alt={service.label} className="object-cover w-full h-full" />
            </div>
            <div className="p-2 flex-1 flex flex-col justify-end">
              <h3 className="font-medium text-xs mb-1 truncate">{service.label}</h3>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default ServicesSection; 